import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant";
import axios from "axios";
import { TENANT_DOMAIN } from "../../utils/domain";

/**
 * @typedef {Object} SetTeacherPasswordParams
 * @property {string} token - Password reset token
 * @property {string} password - New password
 * @property {string} password_confirmation - Confirm password (must match password)
 * @property {string} email - User's email address
 */

/**
 * Custom hook for handling teacher password reset functionality.
 *
 * @description
 * This hook manages the password set process for teacher users, including:
 * - Loading state management
 * - Error handling
 * - API communication with the teacher auth endpoint
 */

export const useTeacherSetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Executes the reset password API request.
   * @async
   * @param {SetTeacherPasswordParams} params - Object containing required reset password information
   * @param {Function} onSuccess - Callback function to execute on successful reset
   * @returns {Promise<void>} Resolves once the API request completes
   */
  const setTeacherPassword = async (params, onSuccess) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${TENANT_DOMAIN}/tenant/auth/set-password`,
        {
          password: params.password,
          password_confirmation: params.password_confirmation,
          token: params.token,
          email: params.email,
        }
      );
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      if (err.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    setError,
    setTeacherPassword,
  };
};
